import { Card, CardContent, Typography, Box, Stack } from '@mui/material';
import { BarChart3 } from 'lucide-react';

interface MonthData {
  month: string;
  count: number;
}

interface ReviewsByMonthChartProps {
  data: MonthData[];
  hasReviews?: boolean;
}

const ReviewsByMonthChart = ({ data, hasReviews = true }: ReviewsByMonthChartProps) => {
  const maxCount = data.length > 0 ? Math.max(...data.map(item => item.count), 1) : 1;

  return (
    <Card
      sx={{
        background: '#FFFFFF',
        border: '1px solid #E4E4E7',
        borderRadius: '20px',
        boxShadow: 'none',
        height: '367px',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {!hasReviews && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.75) 0%, rgba(249, 250, 251, 0.85) 100%)',
            backdropFilter: 'blur(2px)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            textAlign: 'center'
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              boxShadow: '0px 8px 24px rgba(79, 70, 229, 0.25)'
            }}
          >
            <BarChart3 size={32} color="#FFFFFF" />
          </Box>
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '12px',
              p: 3,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(79, 70, 229, 0.1)',
              maxWidth: '320px'
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: '18px',
                lineHeight: '24px',
                color: '#111827',
                mb: 1
              }}
            >
              No Reviews Yet! 📅
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#6B7280'
              }}
            >
              Once your customers start posting reviews, we'll show monthly trends and statistics here!
            </Typography>
          </Box>
        </Box>
      )}
      <CardContent sx={{ p: '24px' }}>
        {/* Title */}
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 700,
            fontSize: '16px',
            lineHeight: '32px',
            letterSpacing: '-0.02em',
            color: '#18181B',
            mb: 2
          }}
        >
          Reviews by Month
        </Typography>

        {/* Bar Chart */}
        <Stack spacing={2.5} sx={{ mt: 3 }}>
          {data.map((item, index) => {
            const percentage = (item.count / maxCount) * 100;
            // Alternate colors between two purple shades
            const barColor = index % 2 === 0 ? '#92487A' : '#540863';
            
            return (
              <Stack key={item.month} direction="row" alignItems="center" spacing={1.5}>
                {/* Month Label */}
                <Typography
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '15px',
                    color: '#030229',
                    opacity: 0.7,
                    minWidth: '35px'
                  }}
                >
                  {item.month}
                </Typography>

                {/* Progress Bar Container */}
                <Box
                  sx={{
                    flex: 1,
                    height: '14px',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {/* Progress Bar */}
                  <Box
                    sx={{
                      width: `${percentage}%`,
                      height: '14px',
                      background: barColor,
                      borderRadius: '0px 30px 30px 0px',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>

                {/* Count Label */}
                <Typography
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: item.count === maxCount ? 600 : 400,
                    fontSize: '12px',
                    lineHeight: '15px',
                    color: '#030229',
                    minWidth: '60px',
                    textAlign: 'right'
                  }}
                >
                  {item.count.toLocaleString()}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ReviewsByMonthChart;

